import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Box } from '@tanium/react-box';
import { TooltippedButton } from '@tanium/react-button';
import { FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { TrashIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { SectionContainer } from '@tanium/react-section-container';
import { Stack, StackChild } from '@tanium/react-stack';
import { StackedRibbonListMinimalStatic, StackedRibbonListWrapper } from '@tanium/react-stacked-ribbon-list';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, FormikProps } from 'formik';
import { useOnInputChange } from '../InputHandlerUtils';
import { alwaysTrueOp } from '../../utilities/commonFuncs';
import { orderBy } from '../../utilities/arrayHelper';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { MediumSubtleText } from '../../TextStyles';

interface LoginDomainFormProps {
  formData: FormikProps<ConfigureIdpRequest>;
}

// Mirrors the server's domainNameRegex (httpgateway/idp.go) exactly, so client and
// server never disagree on validity: each label must start/end alphanumeric and is
// capped at 63 octets (RFC 1035); underscores are rejected.
const IDP_DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
// The DNS spec's max hostname length (RFC 1035); comfortably under the fqdn column's VARCHAR(256)
const MAX_DOMAIN_LENGTH = 253;
const TANIUM_LOCAL_DOMAIN = 'mytanium.com';
const RESERVED_DOMAIN_SUFFIXES = ['tanium.local'];

// Mirrors the server's isReservedLoginFQDN (service/idp.go).
const isReservedDomain = (loginDomain: string): boolean =>
  loginDomain.endsWith(TANIUM_LOCAL_DOMAIN) ||
  RESERVED_DOMAIN_SUFFIXES.some((reserved) => loginDomain === reserved || loginDomain.endsWith(`.${reserved}`));

export const LoginDomainForm: React.VFC<LoginDomainFormProps> = ({ formData }) => {
  const { idpConfigs } = useIdentityProviderConfigs();

  const [loginDomainInput, setLoginDomainInput] = useState<string>('');
  const [validationError, setValidationError] = useState('');

  const domains = useMemo(() => {
    return orderBy(formData.values.domainNames, (a, b) => (a > b ? 1 : -1));
  }, [formData.values.domainNames]);

  const { t } = useTranslation();

  const onAddDomain = useCallback(
    (domain: string) => {
      void formData.setFieldValue('domainNames', [...formData.values.domainNames, domain]);
    },
    [formData]
  );
  const onRemoveDomain = useCallback(
    (domain: string) => {
      void formData.setFieldValue(
        'domainNames',
        formData.values.domainNames.filter((x) => x !== domain)
      );
      // need to validate at least one domain is required when domain is deleted
      // Can't use yup.min validation because it would suppress invalid domain error when first domain is invalid
      if (domains.length <= 1) {
        setValidationError(t('At least one Sign In Email Domain must be provided'));
      }
    },
    [domains.length, formData, t]
  );

  const idpAlreadyUsesDomain = useCallback(
    (domain: string): boolean => {
      const usedDomains = idpConfigs.filter((idp) => idp.id !== formData.values.id).flatMap((x) => x.domainNames);
      const existDomain = [...domains, ...usedDomains].find((x) => x === domain);
      return existDomain !== undefined;
    },
    [domains, formData.values.id, idpConfigs]
  );

  const normalizedLoginDomainInput = useMemo(() => loginDomainInput.trim().toLowerCase(), [loginDomainInput]);

  const addSignInDomainTooltip = useMemo((): string => {
    if (idpAlreadyUsesDomain(normalizedLoginDomainInput)) {
      return t('The specified domain is already in use.');
    }
    return '';
  }, [normalizedLoginDomainInput, idpAlreadyUsesDomain, t]);

  const isValidDomain = useMemo((): boolean => {
    if (!normalizedLoginDomainInput || idpAlreadyUsesDomain(normalizedLoginDomainInput)) {
      // disable Add Domain button if loginDomainInput is empty or already added
      return false;
    }
    return (
      normalizedLoginDomainInput.length <= MAX_DOMAIN_LENGTH &&
      IDP_DOMAIN_REGEX.test(normalizedLoginDomainInput) &&
      !isReservedDomain(normalizedLoginDomainInput)
    );
  }, [normalizedLoginDomainInput, idpAlreadyUsesDomain]);

  // Shows "Invalid domain" only while the current input is non-empty, not a duplicate,
  // and fails validation; clears it the moment typing makes the input valid again,
  // without clobbering the separate "at least one domain" message onRemoveDomain sets.
  useEffect(() => {
    const invalidDomainMsg = t('Invalid domain');
    const shouldShowInvalidDomain =
      Boolean(normalizedLoginDomainInput) && !idpAlreadyUsesDomain(normalizedLoginDomainInput) && !isValidDomain;
    setValidationError((prev) => {
      if (shouldShowInvalidDomain) {
        return invalidDomainMsg;
      }
      return prev === invalidDomainMsg ? '' : prev;
    });
  }, [normalizedLoginDomainInput, isValidDomain, idpAlreadyUsesDomain, t]);

  const onAddDomainHandler = useCallback(() => {
    onAddDomain(normalizedLoginDomainInput);
    setLoginDomainInput('');
    setValidationError('');
  }, [normalizedLoginDomainInput, onAddDomain]);

  const onLoginDomainInputChange = useOnInputChange((value) => setLoginDomainInput(value));

  return (
    <>
      <Stack key={0} direction="row" itemSpacing={2}>
        <StackChild grow={1}>
          <SectionContainer
            sideContent={
              <TooltippedButton
                disabled={!isValidDomain}
                tooltipContent={addSignInDomainTooltip}
                onClick={onAddDomainHandler}
              >
                {t('Add Sign In Domain')}
              </TooltippedButton>
            }
          >
            <Field name="domainNames">
              {({ field }: FieldProps): React.ReactElement => (
                <FormRow {...field} dataKey="domainNames" label={t('Domain')} errors={validationError} required>
                  <FormTextInput data-testid="login-domain-input" onChange={onLoginDomainInputChange} value={loginDomainInput} />
                </FormRow>
              )}
            </Field>
          </SectionContainer>
        </StackChild>
      </Stack>

      <Box>
        <MediumSubtleText>{t('Specify the email domains that can be used to sign in.')}</MediumSubtleText>
        <br />
        <br />
        {domains.length > 0 && (
          <StackedRibbonListWrapper>
            <StackedRibbonListMinimalStatic
              columns={[
                {
                  label: t('Domain'),
                  dataKey: 'domain',
                  isSortDisabled: true,
                  renderer: ({ itemData }: { itemData: string }) => {
                    return itemData;
                  },
                },
                {
                  label: t('Action'),
                  dataKey: 'action',
                  isSortDisabled: true,
                  width: 80,
                  renderer: ({ itemData }: { itemData: string }) => (
                    <IconButton
                      icon={TrashIcon}
                      height={16}
                      width={16}
                      label={t('Remove')}
                      onClick={() => {
                        onRemoveDomain(itemData);
                      }}
                    />
                  ),
                },
              ]}
              dataGetter={(i) => domains[i]}
              isItemLoaded={alwaysTrueOp}
              itemCount={domains.length}
              itemIdGetter={(i) => domains[i]}
            ></StackedRibbonListMinimalStatic>
          </StackedRibbonListWrapper>
        )}
      </Box>
    </>
  );
};
